import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  CheckCircle,
  Globe,
  Heart,
  Key,
  Lock,
  MapPin,
  QrCode,
  Shield,
  Smartphone,
  Star,
  TrendingUp,
  User,
  Wallet,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const IndiaDPI = () => {
  const problemSolutions = [
    {
      problem: 'Citizens carry multiple physical documents',
      solution: 'One digital identity wallet for everything',
      icon: Wallet,
      example:
        'Instead of carrying 10+ cards, citizens have one secure digital wallet with all credentials',
    },
    {
      problem: "Government offices can't verify documents instantly",
      solution: 'Real-time verification in 1 second',
      icon: Zap,
      example:
        'Citizen scans office QR code → Shares verified credential → Officer instantly sees authentic document → No more fake certificates',
    },
    {
      problem: 'Data scattered across different systems',
      solution: 'Connected ecosystem with privacy control',
      icon: Globe,
      example:
        'Your education, health, banking data works together while YOU control who sees what',
    },
    {
      problem: 'Citizens lose control of personal data',
      solution: 'You own and control your identity',
      icon: Lock,
      example:
        'Like having your own personal vault - you decide who gets the key and for how long',
    },
  ];

  const dpiIntegration = [
    {
      name: 'Aadhaar + Documint',
      currentState:
        'Aadhaar remains a static identifier - powerful but isolated, lacking the cryptographic foundation to create unbreakable digital trust chains',
      withDocumint:
        "Government issues Aadhaar as a verifiable credential - catapulting India's digital identity into a cryptographic powerhouse that anchors an entire ecosystem of mathematically linked credentials",
      icon: User,
      analogy:
        'Like evolving from a basic key to a quantum-encrypted master vault that cryptographically proves your entire digital identity fortress',
      benefits: [
        'Documint platform transforms Aadhaar into cryptographic DNA - the foundational verifiable credential that links every achievement to your unbreakable digital identity',
        "Mathematical impossibility of fraud - even quantum computers couldn't fake your credential chain",
        'Instant verification of your complete trust ecosystem with military-grade cryptographic proof',
        'Universal acceptance across all 1.4 billion Indian digital interactions - one identity, infinite possibilities',
      ],
      integrationPoint:
        "Aadhaar's 1.3 billion user foundation + Documint's cryptographic chaining = The world's most trusted identity infrastructure",
      visualExample:
        'Student presents their cryptographic identity fortress → Aadhaar + degree + certificates mathematically linked in unbreakable chain → Officer verifies billion-dollar security in milliseconds → Instant admission with absolute confidence',
    },
    {
      name: 'DigiLocker + Documint',
      currentState:
        "DigiLocker stores your documents - but they're just digital copies",
      withDocumint:
        'Transform DigiLocker into an intelligent document powerhouse where every file becomes self-authenticating and universally verifiable',
      icon: Lock,
      analogy:
        'Like upgrading from a basic cloud folder to an AI-powered security fortress that guards and validates your digital life',
      benefits: [
        'Documents that cryptographically prove their own authenticity without external verification',
        'Instant verification even when issuing authorities are offline or unreachable',
        "Selective disclosure - share only what's needed while keeping sensitive data private",
        'Zero-dependency verification that works anywhere in the world, offline or online',
      ],
      integrationPoint:
        "DigiLocker's cloud storage + Documint's cryptographic signatures = Self-authenticating smart documents",
      visualExample:
        'Employer opens your certificate → Mathematical proof instantly validates authenticity → No calls, no delays, no doubt - hired on the spot',
    },
    {
      name: 'CoWIN + Documint',
      currentState:
        'CoWIN digitized vaccination records - but verification remains complex',
      withDocumint:
        'Transform CoWIN into a global health passport standard, creating unbreakable medical credentials that work seamlessly worldwide',
      icon: Heart,
      analogy:
        'Like upgrading from a local hospital card to a universal health passport accepted by every country on Earth',
      benefits: [
        'Internationally recognized health credentials with cryptographic authenticity',
        'Mathematically impossible to forge, duplicate, or manipulate health records',
        'Complete medical history integration with privacy-first selective sharing',
        'Instant global health verification for travel, employment, and medical care',
      ],
      integrationPoint:
        "CoWIN's health records + Documint's tamper-proof certificates = Global health passport standard",
      visualExample:
        'International traveler approaches immigration → Scanner reads tamper-proof health passport → Instant verification across 195 countries → Seamless global mobility',
    },
  ];

  const realWorldScenarios = [
    {
      title: 'Student Applying for Government Scholarship',
      before: {
        steps: [
          'Visit college office with 8 different certificates',
          'Officer manually verifies each document',
          'Calls previous schools to confirm authenticity',
          'Waits 2-3 weeks for verification',
          'Often documents get lost or damaged',
        ],
        time: '3-4 weeks',
        problems: [
          'Document forgery',
          'Long delays',
          'Manual errors',
          'Lost paperwork',
        ],
      },
      after: {
        steps: [
          'Student opens Documint wallet on phone',
          'Shows QR code with all verified credentials',
          'Officer scans → All documents verified instantly',
          'System auto-checks eligibility criteria',
          'Scholarship approved in real-time',
        ],
        time: '2 minutes',
        benefits: [
          'Zero fraud',
          'Instant verification',
          'No paperwork',
          'Automatic processing',
        ],
      },
      icon: Award,
    },
    {
      title: 'Farmer Applying for Loan',
      before: {
        steps: [
          'Collects land records from village office',
          'Gets income certificate from tehsildar',
          'Visits bank with physical documents',
          'Bank verifies documents manually',
          'Loan processing takes 1-2 months',
        ],
        time: '1-2 months',
        problems: [
          'Document verification delays',
          'Multiple office visits',
          'Risk of rejection',
          'Corruption opportunities',
        ],
      },
      after: {
        steps: [
          'Farmer opens Documint app',
          'All land records, income proof already verified',
          'Shares credentials with bank digitally',
          "Bank's system auto-verifies everything",
          'Loan approved based on verified data',
        ],
        time: 'Same day',
        benefits: [
          'Instant verification',
          'No office visits',
          'Transparent process',
          'Faster credit access',
        ],
      },
      icon: TrendingUp,
    },
    {
      title: 'Doctor Starting Practice in New State',
      before: {
        steps: [
          'Gets degree certificates attested',
          'Submits to medical council',
          'Council verifies with original university',
          'Waits for manual verification process',
          'Gets state registration after verification',
        ],
        time: '3-6 months',
        problems: [
          'Interstate verification delays',
          'Duplicate documentation',
          'Manual processes',
          'Potential for fraud',
        ],
      },
      after: {
        steps: [
          'Doctor shows verified medical degree',
          'Documint proves degree authenticity instantly',
          'Medical council accepts digital verification',
          'Registration process automated',
          'Doctor can start practice immediately',
        ],
        time: '1 day',
        benefits: [
          'Interstate mobility',
          'Instant verification',
          'Reduced bureaucracy',
          'Professional mobility',
        ],
      },
      icon: Heart,
    },
  ];

  const dpiStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    name: 'Documint India Digital Public Infrastructure Integration',
    description:
      "Simple, intuitive explanation of how Documint enhances India's Digital Public Infrastructure to make government services faster, fraud-proof, and citizen-friendly.",
    url: 'https://documint.id/india-dpi',
    provider: {
      '@type': 'Organization',
      name: 'Documint',
      url: 'https://documint.id',
    },
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Government Officials, Policy Makers, Citizens',
    },
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead
        title="How Documint Supercharges India's Digital Public Infrastructure"
        description="Simple explanation of how Documint makes Aadhaar, DigiLocker, UPI smarter with instant verification, fraud prevention, and citizen control. Real examples included."
        keywords="India digital infrastructure, Aadhaar enhancement, DigiLocker smart documents, UPI fraud prevention, Digital India solutions, citizen digital identity, government technology"
        url="https://documint.id/india-dpi"
        structuredData={dpiStructuredData}
      />

      {/* Hero Section - Simple Problem Statement */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Making India{`'`}s Digital Services
                  <span className="block bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
                    {' '}
                    Actually Work for Citizens
                  </span>
                </h1>
              </div>

              <p className="text-2xl text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed">
                <strong>Imagine</strong> if every government service was as easy
                as ordering food online. That{`'`}s what Documint does to India
                {`'`}s digital infrastructure.
              </p>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-orange-200 max-w-4xl mx-auto mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  The Simple Truth
                </h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="text-left">
                    <h4 className="text-lg font-bold text-red-600 mb-3">
                      ❌ Today{`'`}s Reality
                    </h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Citizens carry 10+ different documents</li>
                      <li>• Officers can{`'`}t verify documents instantly</li>
                      <li>• Fake certificates are everywhere</li>
                      <li>• Services take weeks to complete</li>
                    </ul>
                  </div>
                  <div className="text-left">
                    <h4 className="text-lg font-bold text-green-600 mb-3">
                      ✅ With Documint
                    </h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• One phone app for all credentials</li>
                      <li>• Instant verification anywhere</li>
                      <li>• Impossible to fake documents</li>
                      <li>• Services completed in minutes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                Four Big Problems
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
                Documint Solves
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Think of these as the everyday headaches that Documint eliminates
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {problemSolutions.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Problem:
                    </h3>
                    <p className="text-gray-700 mb-4">{item.problem}</p>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h4 className="text-lg font-bold text-green-800 mb-3">
                    Documint{`'`}s Solution:
                  </h4>
                  <p className="text-green-700 font-semibold mb-3">
                    {item.solution}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <p className="text-sm text-gray-700 italic">
                      <strong>Real Example:</strong> {item.example}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How Documint Enhances Each DPI Component */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How Documint Makes India{`'`}s Digital Systems
              <span className="text-orange-600"> 10x Better</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              We don{`'`}t replace what works. We make it work better. Here{`'`}
              s exactly how Documint enhances each part of India{`'`}s digital
              infrastructure.
            </p>
          </motion.div>

          <div className="space-y-16">
            {dpiIntegration.map((integration, index) => (
              <motion.div
                key={integration.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 !== 0 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={`${index % 2 !== 0 ? 'lg:col-start-2' : ''}`}>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200">
                    <div className="flex items-center mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                        <integration.icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {integration.name}
                      </h3>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                        <h4 className="font-semibold text-orange-800 mb-2">
                          Current State:
                        </h4>
                        <p className="text-gray-700">
                          {integration.currentState}
                        </p>
                      </div>

                      <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-2">
                          With Documint:
                        </h4>
                        <p className="text-gray-700 font-semibold">
                          {integration.withDocumint}
                        </p>
                      </div>

                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          Simple Analogy:
                        </h4>
                        <p className="text-gray-700 italic">
                          {integration.analogy}
                        </p>
                      </div>

                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                        <h4 className="font-semibold text-purple-800 mb-3">
                          What This Means for Citizens:
                        </h4>
                        <ul className="space-y-2">
                          {integration.benefits.map((benefit, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-sm text-gray-700"
                            >
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`${index % 2 !== 0 ? 'lg:col-start-1' : ''}`}>
                  <div className="bg-gradient-to-br from-orange-100 to-green-100 rounded-2xl p-8 text-center">
                    <h4 className="text-xl font-bold text-gray-900 mb-4">
                      Real-World Example
                    </h4>
                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Scenario:</strong> {integration.visualExample}
                      </p>
                    </div>
                    <div className="mt-6 flex items-center justify-center space-x-4">
                      <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-green-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {index + 1}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className="text-lg font-semibold text-gray-700 block">
                          Integration Point
                        </span>
                        <span className="text-sm text-gray-600 mt-1 block">
                          {integration.integrationPoint}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Scenarios */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-green-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See the Real Impact: Before vs After Documint
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These are real scenarios that happen millions of times across
              India every day
            </p>
          </motion.div>

          <div className="space-y-16">
            {realWorldScenarios.map((scenario, index) => (
              <motion.div
                key={scenario.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-orange-500 to-green-600 p-6 text-white">
                  <div className="flex items-center">
                    <scenario.icon className="w-8 h-8 mr-4" />
                    <h3 className="text-2xl font-bold">{scenario.title}</h3>
                  </div>
                </div>

                <div className="p-8">
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Before Documint */}
                    <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                      <h4 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                        <span className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
                          ❌
                        </span>
                        Before Documint
                      </h4>

                      <div className="mb-6">
                        <h5 className="font-semibold text-gray-900 mb-3">
                          Process Steps:
                        </h5>
                        <ol className="space-y-2">
                          {scenario.before.steps.map((step, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-sm text-gray-700"
                            >
                              <span className="w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs">
                                {idx + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-red-300">
                          <div className="text-red-600 font-semibold text-sm">
                            Time Required
                          </div>
                          <div className="text-red-800 font-bold">
                            {scenario.before.time}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-red-300">
                          <div className="text-red-600 font-semibold text-sm">
                            Major Problems
                          </div>
                          <div className="text-xs text-red-700 mt-1">
                            {scenario.before.problems.join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* After Documint */}
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <h4 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                        <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
                          ✅
                        </span>
                        After Documint
                      </h4>

                      <div className="mb-6">
                        <h5 className="font-semibold text-gray-900 mb-3">
                          Process Steps:
                        </h5>
                        <ol className="space-y-2">
                          {scenario.after.steps.map((step, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-sm text-gray-700"
                            >
                              <span className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs">
                                {idx + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-green-300">
                          <div className="text-green-600 font-semibold text-sm">
                            Time Required
                          </div>
                          <div className="text-green-800 font-bold">
                            {scenario.after.time}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-green-300">
                          <div className="text-green-600 font-semibold text-sm">
                            Key Benefits
                          </div>
                          <div className="text-xs text-green-700 mt-1">
                            {scenario.after.benefits.join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* India Stack Lego Blocks */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-green-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
                India Stack Reimagined:
              </span>
              <br />
              The Ultimate Digital Architecture
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Witness the{' '}
              <strong>most ambitious digital infrastructure on Earth</strong> -
              where 1.4 billion Indians access government services, make
              payments, and prove their identity. Now watch Documint become the{' '}
              <span className="text-orange-600 font-semibold">
                missing puzzle piece
              </span>{' '}
              that makes it all bulletproof.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            {/* Stack Visualization */}
            <div className="relative">
              {/* Application Layer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mb-6"
              >
                <div className="text-center mb-4">
                  <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold">
                    Application Layer
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-purple-500 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Banking Apps
                  </div>
                  <div className="bg-purple-500 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Government Services
                  </div>
                  <div className="bg-purple-500 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Healthcare Apps
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-green-600 text-white p-4 rounded-lg text-center font-bold shadow-lg border-4 border-yellow-400">
                    Documint Apps
                  </div>
                </div>
              </motion.div>

              {/* Service Layer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mb-6"
              >
                <div className="text-center mb-4">
                  <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">
                    Service Layer
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-500 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    UPI (Payments)
                  </div>
                  <div className="bg-blue-500 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    DigiLocker (Documents)
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-green-600 text-white p-4 rounded-lg text-center font-bold shadow-lg border-4 border-yellow-400">
                    Documint (Verifiable Credentials)
                  </div>
                </div>
              </motion.div>

              {/* Identity Layer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <div className="text-center mb-4">
                  <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                    Identity Layer
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-green-500 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Aadhaar (Digital Identity)
                  </div>
                </div>
              </motion.div>

              {/* Infrastructure Layer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mb-6"
              >
                <div className="text-center mb-4">
                  <span className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full font-semibold">
                    Infrastructure Layer
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-600 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Internet Connectivity
                  </div>
                  <div className="bg-gray-600 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Mobile Networks
                  </div>
                  <div className="bg-gray-600 text-white p-4 rounded-lg text-center font-semibold shadow-lg">
                    Data Centers
                  </div>
                </div>
              </motion.div>

              {/* Connection Lines */}
              <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-400 via-blue-400 via-green-400 to-gray-400 transform -translate-x-1/2 -z-10"></div>
            </div>

            {/* Explanation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 bg-white rounded-2xl p-8 shadow-lg border border-orange-200"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                The Master Stroke: Where Documint Transforms Everything
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                  <h4 className="text-lg font-bold text-orange-600 mb-3">
                    ⚡ Core Infrastructure Power
                  </h4>
                  <p className="text-gray-700 font-medium">
                    Documint doesn{`'`}t just join India Stack -{' '}
                    <strong>it supercharges every layer</strong>. From powering
                    applications to securing Aadhaar itself, Documint becomes
                    the{' '}
                    <span className="text-orange-600">
                      cryptographic backbone
                    </span>{' '}
                    of Digital India.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                  <h4 className="text-lg font-bold text-green-600 mb-3">
                    🎯 Perfect Architectural Fit
                  </h4>
                  <p className="text-gray-700 font-medium">
                    Like the final piece of a 1.4 billion person puzzle,
                    Documint seamlessly integrates with existing systems while{' '}
                    <strong>eliminating every single point of fraud</strong>.
                    India Stack just became{' '}
                    <span className="text-green-600">
                      mathematically unbreakable
                    </span>
                    .
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Simple Tech Explanation */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                The Science Behind the Magic:
              </span>
              <br />
              Unbreakable Digital Truth
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Discover the <strong>revolutionary technology</strong> that makes
              document forgery mathematically impossible. Three breakthrough
              innovations that transform every piece of paper into an{' '}
              <span className="text-blue-600 font-semibold">
                indestructible digital fortress
              </span>
              .
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  💎 Cryptographic Perfection
                </h3>
                <p className="text-gray-600 font-medium">
                  <strong>Military-grade digital signatures</strong> that would
                  take the entire world{`'`}s computers{' '}
                  <span className="text-blue-600">billions of years</span> to
                  fake. Each document becomes mathematically unique.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  ⚡ Lightning-Fast Truth
                </h3>
                <p className="text-gray-600 font-medium">
                  <strong>
                    Instant verification in less than a few seconds
                  </strong>{' '}
                  - faster than blinking. Works{' '}
                  <span className="text-green-600">completely offline</span>,
                  from the Amazon rainforest to the International Space Station.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  🛡️ Ultimate Privacy Fortress
                </h3>
                <p className="text-gray-600 font-medium">
                  <strong>Surgical precision data sharing</strong> - prove you
                  {`'`}re over 18 without revealing you{`'`}re 23. Share{' '}
                  <span className="text-purple-600">
                    exactly what{`'`}s needed
                  </span>
                  , nothing more, nothing less.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-green-50 rounded-2xl p-8 border border-orange-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                🔬 The Transformation Equation
              </h3>
              <div className="text-center text-lg text-gray-700 leading-relaxed">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <span className="bg-white px-4 py-2 rounded-lg border border-orange-300 font-semibold">
                    India DPI Systems
                  </span>
                  <span className="text-2xl">+</span>
                  <span className="bg-gradient-to-r from-orange-500 to-green-600 text-white px-4 py-2 rounded-lg font-semibold">
                    Documint Layer
                  </span>
                  <span className="text-2xl">=</span>
                  <span className="bg-gradient-to-r from-green-400 to-blue-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                    Unbreakable Digital Empire
                  </span>
                </div>
                <p className="text-gray-700 mt-4 font-medium">
                  <strong>Result:</strong> India{`'`}s digital infrastructure
                  becomes the{' '}
                  <span className="text-green-600 font-semibold">
                    most secure and trusted system on the planet
                  </span>{' '}
                  - where fraud is mathematically impossible and trust is
                  automatic.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-green-600">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Transform Citizen Services?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Join the mission to make government services as easy as sending a
              WhatsApp message. Let{`'`}s build Digital India that truly serves
              its citizens.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/contact"
                className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold transition-colors duration-200 inline-flex items-center text-lg"
              >
                Schedule a Demo
                <ArrowRight size={24} className="ml-2" />
              </Link>
              <Link
                to="/case-studies"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-xl font-semibold transition-colors duration-200 inline-flex items-center text-lg"
              >
                See Success Stories
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Smartphone className="w-8 h-8 text-white mx-auto mb-3" />
                <div className="text-lg font-semibold text-white mb-2">
                  Works on Smartphones
                </div>
                <div className="text-white/80 text-sm">
                  Android and iOS compatible - accessible to India{`'`}s growing
                  smartphone users
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Shield className="w-8 h-8 text-white mx-auto mb-3" />
                <div className="text-lg font-semibold text-white mb-2">
                  Government-Grade Security
                </div>
                <div className="text-white/80 text-sm">
                  Meets all Indian government security and compliance standards
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Star className="w-8 h-8 text-white mx-auto mb-3" />
                <div className="text-lg font-semibold text-white mb-2">
                  Proven at Scale
                </div>
                <div className="text-white/80 text-sm">
                  Ready to serve all 1.4 billion Indians from day one
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IndiaDPI;
